import { transformJSONFromOld } from 'api/custom-section/transformer';
import { HttpClient } from 'api/http-client';
import { getPageTemplateBasePath } from './template-path';
import {
  CreatePageTemplatePayload,
  PageType,
  SourceType,
  PageTemplate,
  PageTemplateLite,
  PageTemplatesLightPaginationParams,
  PaginatedPageTemplatesResponse,
  PAGE_TEMPLATES_LIGHT_PAGE_SIZE,
  UpdatePageTemplatePayload,
} from './types';

class TemplateAPIClient extends HttpClient {
  public getPageTemplatesLight<
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(
    pageType: T,
    sourceType: S,
    params: PageTemplatesLightPaginationParams = {},
  ): Promise<PaginatedPageTemplatesResponse<T, S>> {
    const limit = params.limit ?? PAGE_TEMPLATES_LIGHT_PAGE_SIZE;
    const offset = params.offset ?? 0;
    const search = params.search?.trim() || undefined;

    return this.get<PaginatedPageTemplatesResponse<T, S>>(
      `${getPageTemplateBasePath(pageType)}/light`,
      {
        params: {
          sourceType,
          paginated: true,
          limit,
          offset,
          ...(search ? { search } : {}),
        },
      },
    );
  }

  /* c8 ignore start - API methods tested via integration tests */
  public getAllPageTemplates<
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(pageType: T, sourceType: S): Promise<PageTemplateLite<T, S>[]> {
    return this.get<PageTemplateLite<T, S>[]>(
      `${getPageTemplateBasePath(pageType)}/light`,
      {
        params: { sourceType },
      },
    );
  }

  public getPageTemplate<
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(id: number, pageType?: PageType): Promise<PageTemplate<T, S>> {
    return this.get<PageTemplate<T, S>>(
      `${getPageTemplateBasePath(pageType)}/${id}`,
    ).then((response) => ({
      ...response,
      tiptapJson: transformJSONFromOld(response.tiptapJson),
    }));
  }

  public createPageTemplate<
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(
    payload: CreatePageTemplatePayload<T, S>,
    pageType?: PageType,
  ): Promise<PageTemplate<T, S>> {
    return this.post(
      getPageTemplateBasePath(payload.pageType ?? pageType),
      payload,
    );
  }

  public updatePageTemplate<
    T extends PageType = PageType,
    S extends SourceType = SourceType,
  >(
    payload: UpdatePageTemplatePayload<T, S>,
    pageType?: PageType,
  ): Promise<PageTemplate<T, S>> {
    return this.put(
      `${getPageTemplateBasePath(payload.pageType ?? pageType)}/${payload.templateId}`,
      payload,
    );
  }

  public deletePageTemplate(
    templateId: number,
    pageType?: PageType,
  ): Promise<void> {
    return this.delete<void>(
      `${getPageTemplateBasePath(pageType)}/${templateId}`,
    );
  }
  /* c8 ignore stop */

  public updatePageTemplateOrder(
    templateId: number,
    order: number,
    pageType?: PageType,
  ): Promise<void> {
    return this.put<void, { templateId: number; order: number }>(
      `${getPageTemplateBasePath(pageType)}/reorder`,
      {
        templateId,
        order,
      },
    );
  }

  public updatePageTemplateStatus(
    templateId: number,
    status: boolean,
    pageType?: PageType,
  ): Promise<void> {
    return this.put<void, { status: boolean }>(
      `${getPageTemplateBasePath(pageType)}/${templateId}/toggle-status?newStatus=${status}`,
    );
  }

  public async getPageLevelTemplatePreview(
    meetingId: number,
    pageType: PageType,
    payload?: object,
    signal?: AbortSignal,
  ): Promise<any> {
    try {
      return await this.post<any, { tiptapJson: object | undefined }>(
        `pages/preview/${meetingId}?pageType=${pageType}&streaming=true&documentStructureOnly=true`,
        {
          tiptapJson: payload,
        },
        { signal },
      );
    } catch (e) {
      throw new Error('Failed to fetch page preview', { cause: e });
    }
  }

  public async getSectionLevelPreview(
    meetingId: number,
    pageType: PageType,
    payload: object,
    streaming: boolean,
  ): Promise<any> {
    try {
      return await this.post(
        `pages/preview/section/${meetingId}?pageType=${pageType}&streaming=${streaming}`,
        { originalContent: payload },
      );
    } catch (e) {
      throw new Error('Failed to fetch section preview', { cause: e });
    }
  }
}

export const PageTemplateAPI = new TemplateAPIClient();
export { PAGE_TEMPLATES_LIGHT_PAGE_SIZE };
